import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import {
  AnimatedCollapsibleContent,
  Collapsible,
  CollapsibleTrigger,
} from '@/Domain/components/Shared/Collapsible/Collapsible';
import { PrettyJsonViewer } from '@/Domain/components/Shared/JsonViewier/JsonViewier';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { cn } from '@/Domain/utils/cn';
import { SonyAdminDocumentDetailDto } from '@/UI/KnowledgeBase/SonyDocuments/SonyDocuments.dto';

interface SonyDocumentPayloadProps {
  externalId: string;
}

export const SonyDocumentPayload = ({ externalId }: SonyDocumentPayloadProps) => {
  const { t } = useTranslation('sony_admin');
  const [open, setOpen] = useState(false);

  const payloadQuery = useQuery<SonyAdminDocumentDetailDto>({
    queryKey: [QueryKey.sonyAdmin.document, externalId, true],
    enabled: open,
  });

  return (
    <section className="space-y-2">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          className="flex w-full items-center justify-between rounded-xl py-1 text-left"
          data-testid="sony-payload-trigger"
        >
          <h2 className="text-sm font-semibold text-foreground">{t('sheet.payload_section')}</h2>
          <ChevronDown
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
          />
        </CollapsibleTrigger>
        <AnimatedCollapsibleContent open={open}>
          <div className="mt-2 rounded-xl border border-border bg-muted/50">
            {payloadQuery.isLoading ? (
              <div className="flex items-center justify-center p-6">
                <Spinner className="h-5 w-5" />
              </div>
            ) : payloadQuery.data?.payload ? (
              <PrettyJsonViewer data={payloadQuery.data.payload} />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">{t('sheet.payload_empty')}</p>
            )}
          </div>
        </AnimatedCollapsibleContent>
      </Collapsible>
    </section>
  );
};
